import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardList,
  Search,
  Check,
  X,
  Eye,
  BedDouble,
  User,
  Phone,
  LogIn,
  LogOut,
  Clock,
  IndianRupee,
  Bell,
  BellOff,
  AlertTriangle,
  Plus,
  Calendar,
  CreditCard,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/ui/Modal';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import AdminBookingModal from '../../components/admin/AdminBookingModal';
import ChargeAdjustments from '../../components/booking/ChargeAdjustments';
import IdCardViewer from '../../components/admin/IdCardViewer';
import { useAuth } from '../../context/AuthContext';
import { bookingsAPI, roomsAPI, stationsAPI, paymentAPI } from '../../services/api';
import { computeBillTotals } from '../../utils/billing';

// Abbreviated on purpose — "12 Hours" in every row ate width the Contact and Status
// columns needed more.
const SLOT_LABELS = {
  '1hr': '1 hr',
  '3hr': '3 hrs',
  '6hr': '6 hrs',
  '9hr': '9 hrs',
  '12hr': '12 hrs',
  '24hr': '24 hrs'
};

// Status pill: colour plus a short, readable label (the raw values are snake_case).
const STATUS_META = {
  payment_pending: { label: 'Unpaid', className: 'bg-orange-100 text-orange-700 border-orange-200' },
  pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  approved: { label: 'Approved', className: 'bg-green-100 text-green-700 border-green-200' },
  checked_in: { label: 'Checked In', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  checked_out: { label: 'Checked Out', className: 'bg-midnight-100 text-midnight-700 border-midnight-200' },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700 border-red-200' },
  cancelled: { label: 'Cancelled', className: 'bg-midnight-100 text-midnight-500 border-midnight-200' }
};

/** One row action: a small icon and a short label, sized to fill the vertical stack. */
const RowAction = ({ icon: Icon, label, onClick, tone = 'neutral', title }) => {
  const tones = {
    neutral: 'text-midnight-600 border-midnight-200 bg-white hover:bg-midnight-100 hover:border-midnight-300',
    green: 'text-green-700 border-green-200 bg-green-50 hover:bg-green-100 hover:border-green-300',
    blue: 'text-blue-700 border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-300',
    purple: 'text-purple-700 border-purple-200 bg-purple-50 hover:bg-purple-100 hover:border-purple-300',
    red: 'text-red-700 border-red-200 bg-red-50 hover:bg-red-100 hover:border-red-300',
    alarm: 'text-white border-red-500 bg-red-500 hover:bg-red-600 animate-pulse'
  };
  return (
    <button
      onClick={onClick}
      title={title || label}
      className={`w-full flex items-center gap-1.5 px-2 py-1 rounded-md border text-[11px] font-semibold leading-5 transition-colors ${tones[tone]}`}
    >
      <Icon className="w-3 h-3 flex-shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );
};

const BookingRequests = () => {
  const { admin } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedStation, setSelectedStation] = useState('');
  const [stations, setStations] = useState([]);

  // Modal states
  const [isAllocateModalOpen, setIsAllocateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [isAdminBookingModalOpen, setIsAdminBookingModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [extraCharges, setExtraCharges] = useState('');
  const [extraChargesNote, setExtraChargesNote] = useState('');
  const [discount, setDiscount] = useState('');
  const [discountNote, setDiscountNote] = useState('');
  const [checkingOut, setCheckingOut] = useState(false);
  const [syncingPayment, setSyncingPayment] = useState(false);

  // Timer and alarm states
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [dismissedAlarms, setDismissedAlarms] = useState({});
  const [activeAlarms, setActiveAlarms] = useState({});
  const alarmAudioRef = useRef(null);

  useEffect(() => {
    if (admin?.role === 'super_admin') {
      fetchStations();
    } else if (admin?.assignedStation) {
      setSelectedStation(admin.assignedStation._id);
    }
  }, [admin]);

  useEffect(() => {
    if (selectedStation) {
      fetchData();
    }
  }, [selectedStation, filterStatus]);

  // Initialize audio on mount
  useEffect(() => {
    alarmAudioRef.current = new Audio('/alarm-clock-beep-105903.mp3');
    alarmAudioRef.current.loop = true;

    return () => {
      if (alarmAudioRef.current) {
        alarmAudioRef.current.pause();
        alarmAudioRef.current = null;
      }
    };
  }, []);

  // Auto-refresh bookings every 30 seconds
  useEffect(() => {
    if (!selectedStation) return;

    const refreshInterval = setInterval(() => {
      // Silent refresh without showing loading spinner
      const fetchSilently = async () => {
        try {
          const params = { station: selectedStation };
          if (filterStatus) params.status = filterStatus;

          const [bookingsRes, roomsRes] = await Promise.all([
            bookingsAPI.getRequests(params),
            roomsAPI.getAll({ station: selectedStation, status: 'available' })
          ]);

          setBookings(bookingsRes.data.bookings);
          setRooms(roomsRes.data.rooms);
        } catch (error) {
          console.error('Error refreshing data:', error);
        }
      };

      fetchSilently();
    }, 30000); // 30 seconds

    return () => clearInterval(refreshInterval);
  }, [selectedStation, filterStatus]);

  // Update current time every second for countdown timers
  useEffect(() => {
    const timerInterval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(timerInterval);
  }, []);

  // Check for expired bookings and trigger alarms
  useEffect(() => {
    const checkedInBookings = bookings.filter(b => b.status === 'checked_in' && b.expectedCheckoutAt);

    checkedInBookings.forEach(booking => {
      const remaining = new Date(booking.expectedCheckoutAt) - currentTime;

      if (remaining <= 0 && !dismissedAlarms[booking._id] && !activeAlarms[booking._id]) {
        // Timer expired - trigger alarm
        setActiveAlarms(prev => ({ ...prev, [booking._id]: true }));

        // Play audio if not already playing
        if (alarmAudioRef.current && alarmAudioRef.current.paused) {
          alarmAudioRef.current.play().catch(err => {
            console.error('Error playing alarm:', err);
          });
        }
      }
    });

    // Stop audio if all alarms are dismissed
    const hasActiveAlarm = Object.values(activeAlarms).some(v => v);
    if (!hasActiveAlarm && alarmAudioRef.current && !alarmAudioRef.current.paused) {
      alarmAudioRef.current.pause();
      alarmAudioRef.current.currentTime = 0;
    }
  }, [currentTime, bookings, dismissedAlarms, activeAlarms]);

  // Helper function to format remaining time
  const formatRemainingTime = useCallback((expectedCheckoutAt) => {
    const remaining = new Date(expectedCheckoutAt) - currentTime;

    if (remaining <= 0) {
      return { text: 'OVERDUE', isOverdue: true };
    }

    const hours = Math.floor(remaining / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    return {
      text: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
      isOverdue: false
    };
  }, [currentTime]);

  // Dismiss alarm for a specific booking
  const dismissAlarm = useCallback((bookingId) => {
    setDismissedAlarms(prev => ({ ...prev, [bookingId]: true }));
    setActiveAlarms(prev => ({ ...prev, [bookingId]: false }));

    // Check if there are any other active alarms
    const otherActiveAlarms = Object.entries(activeAlarms).filter(
      ([id, active]) => id !== bookingId && active
    );

    if (otherActiveAlarms.length === 0 && alarmAudioRef.current) {
      alarmAudioRef.current.pause();
      alarmAudioRef.current.currentTime = 0;
    }

    toast.success('Alarm dismissed');
  }, [activeAlarms]);

  const fetchStations = async () => {
    try {
      const response = await stationsAPI.getAll();
      setStations(response.data.stations);
      if (response.data.stations.length > 0) {
        setSelectedStation(response.data.stations[0]._id);
      }
    } catch (error) {
      console.error('Error fetching stations:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { station: selectedStation };
      if (filterStatus) params.status = filterStatus;

      const [bookingsRes, roomsRes] = await Promise.all([
        bookingsAPI.getRequests(params),
        roomsAPI.getAll({ station: selectedStation, status: 'available' })
      ]);

      setBookings(bookingsRes.data.bookings);
      setRooms(roomsRes.data.rooms);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAllocateModal = (booking) => {
    setSelectedBooking(booking);
    setSelectedRoom('');
    setAdminNotes('');
    setIsAllocateModalOpen(true);
  };

  const handleOpenViewModal = (booking) => {
    setSelectedBooking(booking);
    setIsViewModalOpen(true);
  };

  // Re-checks the transaction with PhonePe directly — for when a guest paid but the
  // webhook never reached us (or arrived before the booking record was ready), leaving
  // paymentStatus stuck at PENDING even though the money went through.
  const handleSyncPayment = async () => {
    if (!selectedBooking?.merchantOrderId) return;
    setSyncingPayment(true);
    try {
      const response = await paymentAPI.checkStatus(selectedBooking.merchantOrderId);
      const { status } = response.data;
      if (status === 'COMPLETED') {
        toast.success('Payment verified as completed');
      } else if (status === 'FAILED') {
        toast.error('Payment verified as failed');
      } else {
        toast('Payment is still pending at PhonePe', { icon: 'ℹ️' });
      }
      const [bookingsRes] = await Promise.all([bookingsAPI.getRequests({ station: selectedStation, ...(filterStatus ? { status: filterStatus } : {}) })]);
      setBookings(bookingsRes.data.bookings);
      const refreshed = bookingsRes.data.bookings.find((b) => b._id === selectedBooking._id);
      if (refreshed) setSelectedBooking(refreshed);
    } catch (error) {
      console.error('Error syncing payment status:', error);
      toast.error(error.response?.data?.error || 'Failed to check payment status');
    } finally {
      setSyncingPayment(false);
    }
  };

  const handleAllocate = async () => {
    if (!selectedRoom) {
      toast.error('Please select a room');
      return;
    }

    try {
      await bookingsAPI.allocate({
        bookingId: selectedBooking._id,
        roomId: selectedRoom,
        adminNotes
      });

      toast.success('Room allocated successfully');
      setIsAllocateModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error allocating room:', error);
      toast.error(error.response?.data?.error || 'Failed to allocate room');
    }
  };

  const handleReject = async (booking) => {
    const reason = window.prompt('Please provide a reason for rejection:');
    if (reason === null) return;

    try {
      await bookingsAPI.reject({
        bookingId: booking._id,
        rejectionReason: reason || 'No rooms available'
      });

      toast.success('Booking rejected');
      fetchData();
    } catch (error) {
      console.error('Error rejecting booking:', error);
      toast.error('Failed to reject booking');
    }
  };

  const handleCheckIn = async (booking) => {
    try {
      await bookingsAPI.checkIn(booking._id);
      toast.success('Guest checked in successfully');
      fetchData();
    } catch (error) {
      console.error('Error checking in:', error);
      toast.error(error.response?.data?.error || 'Failed to check in');
    }
  };

  const handleOpenCheckoutModal = (booking) => {
    setSelectedBooking(booking);
    setExtraCharges('');
    setExtraChargesNote('');
    setDiscount('');
    setDiscountNote('');
    setIsCheckoutModalOpen(true);
  };

  const handleCheckOut = async () => {
    if (checkingOut) return;

    // Guard here as well as on the server: a discount larger than the bill would otherwise
    // only be caught after the guest has been told they are checked out.
    if (checkoutBill.grossAmount > 0 && (parseFloat(discount) || 0) > checkoutBill.grossAmount) {
      toast.error(`Discount cannot exceed the bill total of ₹${checkoutBill.grossAmount}`);
      return;
    }

    setCheckingOut(true);
    try {
      const response = await bookingsAPI.checkOut(selectedBooking._id, {
        extraCharges: parseFloat(extraCharges) || 0,
        extraChargesNote,
        discount: parseFloat(discount) || 0,
        discountNote
      });
      toast.success(`Guest checked out. Total: ₹${response.data.billing.totalAmount}`);
      setIsCheckoutModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error checking out:', error);
      toast.error(error.response?.data?.error || 'Failed to check out');
    } finally {
      setCheckingOut(false);
    }
  };

  // Live invoice preview for the checkout modal, computed with the same helper the server
  // uses so the number shown is the number stored.
  const checkoutBill = computeBillTotals({
    subtotal: selectedBooking?.subtotal ?? selectedBooking?.price ?? 0,
    extraCharges,
    discount,
    gstPercentage: selectedBooking?.gstPercentage ?? 0
  });

  const getStatusBadge = (status) => {
    const styles = {
      payment_pending: 'badge-warning',
      pending: 'badge-warning',
      approved: 'badge-success',
      checked_in: 'badge-info',
      checked_out: 'badge-neutral',
      rejected: 'badge-danger',
      cancelled: 'badge-neutral'
    };
    return styles[status] || 'badge-neutral';
  };

  const getRoomPrice = (room, durationSlot) => {
    if (!room?.category?.pricing) return room?.category?.basePrice || 0;
    return room.category.pricing[durationSlot] || room.category.basePrice;
  };

  const filteredBookings = bookings.filter((booking) => {
    const term = search.toLowerCase();
    return booking.guestName.toLowerCase().includes(term) ||
      booking.mobile.includes(search) ||
      booking.prn?.toLowerCase().includes(term) ||
      booking.merchantOrderId?.toLowerCase().includes(term);
  });

  // The station currently being worked on. A station admin has exactly one; a super admin
  // has whichever they picked in the dropdown beside "Create Booking".
  const activeStation = admin?.role === 'super_admin'
    ? stations.find((s) => s._id === selectedStation) || null
    : admin?.assignedStation || null;

  const overdueCount = filteredBookings.filter(
    (b) => b.status === 'checked_in' && b.expectedCheckoutAt && new Date(b.expectedCheckoutAt) <= currentTime
  ).length;

  // Check if any alarm is active
  const hasAnyActiveAlarm = Object.values(activeAlarms).some(v => v);

  // Stop all alarms
  const stopAllAlarms = useCallback(() => {
    // Dismiss all active alarms
    const newDismissed = { ...dismissedAlarms };
    Object.keys(activeAlarms).forEach(id => {
      if (activeAlarms[id]) {
        newDismissed[id] = true;
      }
    });
    setDismissedAlarms(newDismissed);
    setActiveAlarms({});

    // Stop audio
    if (alarmAudioRef.current) {
      alarmAudioRef.current.pause();
      alarmAudioRef.current.currentTime = 0;
    }

    toast.success('All alarms stopped');
  }, [activeAlarms, dismissedAlarms]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Global Alarm Banner */}
        {hasAnyActiveAlarm && (
          <div className="bg-red-500 text-white px-6 py-4 rounded-xl shadow-lg flex items-center justify-between animate-pulse">
            <div className="flex items-center gap-3">
              <Bell className="w-6 h-6" />
              <div>
                <p className="font-bold text-lg">⏰ Timer Alert!</p>
                <p className="text-sm text-red-100">One or more guests have exceeded their checkout time</p>
              </div>
            </div>
            <button
              onClick={stopAllAlarms}
              className="flex items-center gap-2 bg-white text-red-600 px-4 py-2 rounded-lg font-semibold hover:bg-red-50 transition-colors"
            >
              <BellOff className="w-5 h-5" />
              Stop All Alarms
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-midnight-900">
              Booking Requests
            </h1>
            <p className="text-midnight-600 mt-1">
              Manage and allocate room bookings
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/admin/schedule"
              className="btn-secondary flex items-center gap-2"
            >
              <Calendar className="w-4 h-4" />
              View Schedule
            </Link>
            {admin?.role === 'super_admin' && (
              <select
                value={selectedStation}
                onChange={(e) => setSelectedStation(e.target.value)}
                aria-label="Station to manage"
                className="input-field max-w-xs"
              >
                {stations.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name} ({s.code})
                  </option>
                ))}
              </select>
            )}
            {selectedStation && (
              <button
                onClick={() => setIsAdminBookingModalOpen(true)}
                className="btn-primary flex items-center gap-2"
                title={activeStation ? `Create a booking at ${activeStation.name}` : 'Create a booking'}
              >
                <Plus className="w-5 h-5" />
                <span>Create Booking</span>
                {/* The station is spelled out on the button itself so a super admin can see
                    which one the dropdown has selected before opening the form. */}
                {admin?.role === 'super_admin' && activeStation && (
                  <span className="hidden sm:inline px-1.5 py-0.5 rounded bg-white/20 text-xs font-semibold">
                    {activeStation.code}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-midnight-400" />
            <input
              type="text"
              placeholder="Search by name, mobile, PNR, or order ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-12"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input-field max-w-xs"
          >
            <option value="">All Status (Paid)</option>
            <option value="payment_pending">Payment Pending</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="checked_in">Checked In</option>
            <option value="checked_out">Checked Out</option>
            <option value="rejected">Rejected</option>
          </select>

          {/* Counts, so a filtered or searched view says how much it is actually showing —
              and flags overdue guests even when they are scrolled out of sight. */}
          {!loading && (
            <div className="flex items-center gap-3 text-sm text-midnight-500 sm:ml-auto whitespace-nowrap">
              <span>
                <strong className="text-midnight-800">{filteredBookings.length}</strong>
                {filteredBookings.length !== bookings.length && ` of ${bookings.length}`}
                {' '}booking{filteredBookings.length === 1 ? '' : 's'}
              </span>
              {overdueCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                  <AlertTriangle className="w-3 h-3" />
                  {overdueCount} overdue
                </span>
              )}
            </div>
          )}
        </div>

        {/* Bookings Table */}
        <div className="card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="text-center py-20">
              <ClipboardList className="w-16 h-16 text-midnight-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-midnight-700">No bookings found</h3>
              <p className="text-midnight-500 mt-2">Booking requests will appear here</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* Ten columns were fighting over the width. Category now sits under the room
                  number and the countdown under the status badge, which buys Contact and
                  Status the room they were missing. */}
              <table className="w-full min-w-[960px]">
                <thead className="bg-midnight-50 border-b border-midnight-100">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-midnight-500">Guest</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wide text-midnight-500">Contact</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wide text-midnight-500">Schedule</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wide text-midnight-500">Stay</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wide text-midnight-500">Room</th>
                    <th className="text-right px-3 py-3 text-xs font-semibold uppercase tracking-wide text-midnight-500">Amount</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wide text-midnight-500">Status</th>
                    {/* Pinned to the right edge: the actions are why anyone opens this page,
                        so they stay reachable however far the table is scrolled. */}
                    <th className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wide text-midnight-500 w-px sticky right-0 bg-midnight-50 border-l border-midnight-200 shadow-[-8px_0_10px_-8px_rgba(15,23,42,0.18)]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-midnight-100">
                  {filteredBookings.map((booking) => {
                    const status = STATUS_META[booking.status] || {
                      label: booking.status.replace(/_/g, ' '),
                      className: 'bg-midnight-100 text-midnight-700 border-midnight-200'
                    };
                    const timer = booking.status === 'checked_in' && booking.expectedCheckoutAt
                      ? formatRemainingTime(booking.expectedCheckoutAt)
                      : null;
                    const hasAlarm = Boolean(activeAlarms[booking._id]);
                    const endingSoon = timer && !timer.isOverdue &&
                      new Date(booking.expectedCheckoutAt) - currentTime < 600000;
                    const categoryName = booking.allocatedCategory?.name || booking.requestedCategory?.name;

                    return (
                      <tr
                        key={booking._id}
                        // An overdue guest is the one thing on this page that needs acting on
                        // now, so the whole row is tinted rather than just the timer cell.
                        className={`transition-colors ${timer?.isOverdue
                          ? 'bg-red-50/70 hover:bg-red-50 border-l-4 border-l-red-500'
                          : 'hover:bg-midnight-50 border-l-4 border-l-transparent'
                          }`}
                      >
                        {/* Guest */}
                        <td className="px-4 py-3 align-top">
                          <div className="flex items-start gap-2.5">
                            <div className="w-9 h-9 bg-railway-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="font-bold text-railway-600 text-sm">
                                {booking.guestName.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <div className="font-semibold text-midnight-900 truncate max-w-[11rem]">
                                {booking.guestName}
                              </div>
                              <div className="text-xs text-midnight-500 font-mono">PNR {booking.prn}</div>
                            </div>
                          </div>
                        </td>

                        {/* Contact — never wrapped mid-number */}
                        <td className="px-3 py-3 align-top whitespace-nowrap">
                          <a
                            href={`tel:${booking.countryCode || '+91'}${booking.mobile}`}
                            className="inline-flex items-center gap-1.5 text-midnight-700 hover:text-railway-600 transition-colors"
                            title="Call this guest"
                          >
                            <Phone className="w-3.5 h-3.5 text-midnight-400" />
                            <span className="text-sm font-medium">
                              {booking.countryCode || '+91'} {booking.mobile}
                            </span>
                          </a>
                        </td>

                        {/* Schedule */}
                        <td className="px-3 py-3 align-top whitespace-nowrap">
                          {booking.isAdvanceBooking && booking.scheduledDate && booking.scheduledTimeSlot ? (
                            <div>
                              <div className="text-sm font-medium text-railway-700">
                                {new Date(booking.scheduledDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                <span className="text-midnight-400 mx-1">·</span>
                                {booking.scheduledTimeSlot}
                              </div>
                              <div className="text-xs text-midnight-400">Advance</div>
                            </div>
                          ) : (
                            <span className="text-xs text-midnight-500">Walk-in</span>
                          )}
                        </td>

                        {/* Stay length */}
                        <td className="px-3 py-3 align-top whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-midnight-100 text-midnight-700 text-xs font-semibold">
                            <Clock className="w-3 h-3 text-railway-500" />
                            {SLOT_LABELS[booking.durationSlot] || '12 hrs'}
                          </span>
                        </td>

                        {/* Room, with its category underneath */}
                        <td className="px-3 py-3 align-top">
                          {booking.allocatedRoom ? (
                            <div>
                              <div className="font-semibold text-midnight-900 text-sm">
                                {booking.allocatedRoom.roomNumber}
                              </div>
                              {categoryName && (
                                <div className="text-xs text-midnight-500 truncate max-w-[9rem]">{categoryName}</div>
                              )}
                            </div>
                          ) : (
                            <div>
                              <div className="text-xs text-midnight-400 italic">Not assigned</div>
                              {categoryName && (
                                <div className="text-xs text-midnight-500 truncate max-w-[9rem]">{categoryName}</div>
                              )}
                            </div>
                          )}
                        </td>

                        {/* Amount */}
                        <td className="px-3 py-3 align-top text-right whitespace-nowrap">
                          {booking.totalAmount > 0 || booking.price > 0 ? (
                            <div>
                              <div className="inline-flex items-center gap-0.5 font-semibold text-midnight-900 text-sm">
                                <IndianRupee className="w-3 h-3" />
                                <span>{(booking.totalAmount || booking.price).toLocaleString('en-IN')}</span>
                              </div>
                              <div className={`text-xs font-medium ${booking.paymentStatus === 'COMPLETED' ? 'text-green-600' :
                                booking.paymentStatus === 'FAILED' ? 'text-red-500' : 'text-orange-500'
                                }`}>
                                {booking.paymentStatus === 'COMPLETED' ? 'Paid' :
                                  booking.paymentStatus === 'FAILED' ? 'Failed' : 'Unpaid'}
                              </div>
                            </div>
                          ) : (
                            <span className="text-midnight-400">—</span>
                          )}
                        </td>

                        {/* Status, with the live countdown beneath it */}
                        <td className="px-3 py-3 align-top whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 mx-2 rounded-full border text-xs font-semibold ${status.className}`}>
                            {status.label}
                          </span>
                          {timer && (
                            timer.isOverdue ? (
                              <div className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-red-600">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                OVERDUE
                              </div>
                            ) : (
                              <div className={`mt-1.5 inline-flex items-center gap-1 font-mono text-xs font-medium ${endingSoon ? 'text-orange-600' : 'text-blue-600'
                                }`}>
                                <Clock className="w-3.5 h-3.5" />
                                {timer.text}
                                {endingSoon && <span className="ml-0.5 font-sans not-italic">left</span>}
                              </div>
                            )
                          )}
                        </td>

                        {/* Actions — stacked, labelled, so nobody has to decode an icon */}
                        <td className={`px-3 py-3 align-top sticky right-0 border-l border-midnight-200 shadow-[-8px_0_10px_-8px_rgba(15,23,42,0.18)] ${timer?.isOverdue ? 'bg-red-50' : 'bg-white'
                          }`}>
                          <div className="flex flex-col gap-1 w-[6.5rem]">
                            <RowAction
                              icon={Eye}
                              label="Details"
                              onClick={() => handleOpenViewModal(booking)}
                            />

                            {booking.status === 'pending' && (
                              <>
                                <RowAction
                                  icon={Check}
                                  label="Allocate"
                                  tone="green"
                                  title="Allocate a room"
                                  onClick={() => handleOpenAllocateModal(booking)}
                                />
                                <RowAction
                                  icon={X}
                                  label="Reject"
                                  tone="red"
                                  onClick={() => handleReject(booking)}
                                />
                              </>
                            )}

                            {booking.status === 'approved' && (
                              <RowAction
                                icon={LogIn}
                                label="Check-In"
                                tone="blue"
                                onClick={() => handleCheckIn(booking)}
                              />
                            )}

                            {booking.status === 'checked_in' && (
                              <RowAction
                                icon={LogOut}
                                label="Check-Out"
                                tone="purple"
                                onClick={() => handleOpenCheckoutModal(booking)}
                              />
                            )}

                            {hasAlarm && (
                              <RowAction
                                icon={BellOff}
                                label="Mute"
                                tone="alarm"
                                title="Dismiss this guest's overdue alarm"
                                onClick={() => dismissAlarm(booking._id)}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Allocate Room Modal */}
      <Modal
        isOpen={isAllocateModalOpen}
        onClose={() => setIsAllocateModalOpen(false)}
        title="Allocate Room"
      >
        <div className="space-y-4">
          {selectedBooking && (
            <div className="p-4 bg-midnight-50 rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                <User className="w-5 h-5 text-midnight-500" />
                <span className="font-semibold text-midnight-900">{selectedBooking.guestName}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-midnight-500">PNR:</span>
                  <span className="ml-2 text-midnight-900">{selectedBooking.prn}</span>
                </div>
                <div>
                  <span className="text-midnight-500">Mobile:</span>
                  <span className="ml-2 text-midnight-900">{selectedBooking.countryCode || '+91'} {selectedBooking.mobile}</span>
                </div>
                <div>
                  <span className="text-midnight-500">Duration:</span>
                  <span className="ml-2 text-midnight-900 font-medium">
                    {SLOT_LABELS[selectedBooking.durationSlot] || '12 Hours'}
                  </span>
                </div>
                <div>
                  <span className="text-midnight-500">Requested:</span>
                  <span className="ml-2 text-midnight-900">
                    {selectedBooking.requestedCategory?.name || 'Any Category'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-2">
              Select Room *
            </label>
            {(() => {
              const requestedCategoryRooms = selectedBooking?.requestedCategory
                ? rooms.filter(room => room.category?._id === selectedBooking.requestedCategory._id || room.category === selectedBooking.requestedCategory._id)
                : rooms;
              const hasRequestedCategoryRooms = requestedCategoryRooms.length > 0;
              const displayRooms = hasRequestedCategoryRooms ? requestedCategoryRooms : rooms;

              return (
                <>
                  <select
                    value={selectedRoom}
                    onChange={(e) => setSelectedRoom(e.target.value)}
                    className="input-field"
                    required
                  >
                    <option value="">Choose a room...</option>
                    {displayRooms.map((room) => (
                      <option key={room._id} value={room._id}>
                        {room.roomNumber} - {room.category?.name} (₹{getRoomPrice(room, selectedBooking?.durationSlot || '12hr')})
                      </option>
                    ))}
                  </select>
                  {!hasRequestedCategoryRooms && rooms.length > 0 && (
                    <p className="text-amber-600 text-sm mt-1">
                      ⚠️ No available rooms in requested category ({selectedBooking?.requestedCategory?.name}). Showing other categories.
                    </p>
                  )}
                  {rooms.length === 0 && (
                    <p className="text-red-500 text-sm mt-1">No available rooms</p>
                  )}
                </>
              );
            })()}
          </div>

          {/* Price Preview */}
          {selectedRoom && selectedBooking && (
            <div className="p-4 bg-green-50 rounded-xl border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">Price for {SLOT_LABELS[selectedBooking.durationSlot]}</p>
                  <p className="text-xs text-green-600">
                    {rooms.find(r => r._id === selectedRoom)?.category?.name}
                  </p>
                </div>
                <div className="flex items-center gap-1 text-2xl font-bold text-green-700">
                  <IndianRupee className="w-5 h-5" />
                  <span>{getRoomPrice(rooms.find(r => r._id === selectedRoom), selectedBooking.durationSlot)}</span>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-2">
              Admin Notes (Optional)
            </label>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Any notes for this booking..."
              className="input-field"
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={() => setIsAllocateModalOpen(false)}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              onClick={handleAllocate}
              className="btn-primary flex-1"
              disabled={!selectedRoom}
            >
              Allocate Room
            </button>
          </div>
        </div>
      </Modal>

      {/* View Details Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Booking Details"
        size="lg"
      >
        {selectedBooking && (
          <div className="space-y-6">
            {/* Guest Info */}
            <div className="p-4 bg-midnight-50 rounded-xl">
              <h3 className="font-semibold text-midnight-900 mb-3">Guest Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-midnight-500">Name</p>
                  <p className="font-medium text-midnight-900">{selectedBooking.guestName}</p>
                </div>
                <div>
                  <p className="text-midnight-500">PNR</p>
                  <p className="font-medium text-midnight-900">{selectedBooking.prn}</p>
                </div>
                <div>
                  <p className="text-midnight-500">Mobile</p>
                  <p className="font-medium text-midnight-900">{selectedBooking.countryCode || '+91'} {selectedBooking.mobile}</p>
                </div>
                <div>
                  <p className="text-midnight-500">Status</p>
                  <span className={getStatusBadge(selectedBooking.status)}>
                    {STATUS_META[selectedBooking.status]?.label || selectedBooking.status.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>
            </div>

            {/* ID Proof */}
            {(selectedBooking.idCardImage || selectedBooking.hasIdCardImage || selectedBooking.idCardNumber) && (
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  ID Proof
                </h3>
                <div className="space-y-3">
                  {selectedBooking.idCardNumber && (
                    <div className="text-sm">
                      <p className="text-amber-600 mb-1">ID Card Number</p>
                      <p className="font-bold text-amber-900 text-lg tracking-wide bg-white px-3 py-2 rounded-lg border border-amber-200 inline-block">
                        {selectedBooking.idCardNumber}
                      </p>
                    </div>
                  )}
                  {(selectedBooking.idCardImage || selectedBooking.hasIdCardImage) && (
                    <div className="text-sm">
                      <p className="text-amber-600 mb-1">ID Card Image</p>
                      {/* Fetched through the authenticated route — never a public URL. */}
                      <IdCardViewer bookingId={selectedBooking._id} />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment Info */}
            {(selectedBooking.totalAmount > 0 || selectedBooking.paymentStatus) && (
              <div className={`p-4 rounded-xl border ${selectedBooking.paymentStatus === 'COMPLETED'
                ? 'bg-green-50 border-green-200'
                : selectedBooking.paymentStatus === 'FAILED'
                  ? 'bg-red-50 border-red-200'
                  : 'bg-orange-50 border-orange-200'
                }`}>
                <h3 className={`font-semibold mb-3 flex items-center gap-2 ${selectedBooking.paymentStatus === 'COMPLETED' ? 'text-green-900' :
                  selectedBooking.paymentStatus === 'FAILED' ? 'text-red-900' :
                    'text-orange-900'
                  }`}>
                  <IndianRupee className="w-4 h-4" />
                  Payment Information
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="opacity-70">Payment Status</p>
                    <p className={`font-bold text-lg ${selectedBooking.paymentStatus === 'COMPLETED' ? 'text-green-700' :
                      selectedBooking.paymentStatus === 'FAILED' ? 'text-red-700' :
                        'text-orange-700'
                      }`}>
                      {selectedBooking.paymentStatus === 'COMPLETED' ? 'Paid' :
                        selectedBooking.paymentStatus === 'FAILED' ? 'Failed' :
                          'Pending'}
                    </p>
                  </div>
                  <div>
                    <p className="opacity-70">Amount</p>
                    <p className="font-bold text-lg flex items-center gap-0.5">
                      <IndianRupee className="w-4 h-4" />
                      {selectedBooking.totalAmount || selectedBooking.price || '—'}
                    </p>
                  </div>
                  {selectedBooking.merchantOrderId && (
                    <div className="col-span-2">
                      <p className="opacity-70">Merchant Order ID</p>
                      <p className="font-mono text-xs break-all">{selectedBooking.merchantOrderId}</p>
                    </div>
                  )}
                  {selectedBooking.phonePeOrderId && (
                    <div className="col-span-2">
                      <p className="opacity-70">PhonePe Order ID</p>
                      <p className="font-mono text-xs break-all">{selectedBooking.phonePeOrderId}</p>
                    </div>
                  )}
                  {selectedBooking.paymentAmount != null && (
                    <div>
                      <p className="opacity-70">Amount Charged</p>
                      <p className="font-bold flex items-center gap-0.5">
                        <IndianRupee className="w-3.5 h-3.5" />
                        {selectedBooking.paymentAmount}
                      </p>
                    </div>
                  )}
                </div>
                {selectedBooking.merchantOrderId && (
                  <button
                    onClick={handleSyncPayment}
                    disabled={syncingPayment}
                    className="mt-3 flex items-center gap-2 text-sm font-medium text-railway-600 hover:text-railway-700 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${syncingPayment ? 'animate-spin' : ''}`} />
                    {syncingPayment ? 'Checking with PhonePe...' : 'Sync Payment Status'}
                  </button>
                )}
              </div>
            )}

            {/* Duration Info */}
            <div className="p-4 bg-railway-50 rounded-xl border border-railway-200">
              <h3 className="font-semibold text-railway-900 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Duration Details
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-railway-600">Selected Duration</p>
                  <p className="font-bold text-railway-900 text-lg">
                    {SLOT_LABELS[selectedBooking.durationSlot] || '12 Hours'}
                  </p>
                </div>
                <div>
                  <p className="text-railway-600">Price</p>
                  <p className="font-bold text-railway-900 text-lg flex items-center gap-0.5">
                    <IndianRupee className="w-4 h-4" />
                    {selectedBooking.price || '—'}
                  </p>
                </div>
                {selectedBooking.expectedCheckoutAt && (
                  <div className="col-span-2">
                    <p className="text-railway-600">Expected Checkout</p>
                    <p className="font-medium text-railway-900">
                      {new Date(selectedBooking.expectedCheckoutAt).toLocaleString('en-IN')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Room Info */}
            {selectedBooking.allocatedRoom && (
              <div className="p-4 bg-green-50 rounded-xl">
                <h3 className="font-semibold text-green-900 mb-3">Allocated Room</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-green-600">Room Number</p>
                    <p className="font-bold text-green-900">{selectedBooking.allocatedRoom.roomNumber}</p>
                  </div>
                  <div>
                    <p className="text-green-600">Category</p>
                    <p className="font-medium text-green-900">{selectedBooking.allocatedCategory?.name}</p>
                  </div>
                  <div>
                    <p className="text-green-600">Floor</p>
                    <p className="font-medium text-green-900">{selectedBooking.allocatedRoom.floor || 'Ground'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Timeline */}
            <div className="p-4 bg-white border border-midnight-200 rounded-xl">
              <h3 className="font-semibold text-midnight-900 mb-3">Timeline</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-midnight-500">Requested At</span>
                  <span className="text-midnight-900">
                    {new Date(selectedBooking.requestedAt).toLocaleString('en-IN')}
                  </span>
                </div>
                {selectedBooking.allocatedAt && (
                  <div className="flex justify-between">
                    <span className="text-midnight-500">Allocated At</span>
                    <span className="text-midnight-900">
                      {new Date(selectedBooking.allocatedAt).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
                {selectedBooking.checkInAt && (
                  <div className="flex justify-between">
                    <span className="text-midnight-500">Checked In</span>
                    <span className="text-midnight-900">
                      {new Date(selectedBooking.checkInAt).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
                {selectedBooking.checkOutAt && (
                  <div className="flex justify-between">
                    <span className="text-midnight-500">Checked Out</span>
                    <span className="text-midnight-900">
                      {new Date(selectedBooking.checkOutAt).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Billing (if checked out) */}
            {selectedBooking.status === 'checked_out' && selectedBooking.totalAmount > 0 && (
              <div className="p-4 bg-purple-50 rounded-xl">
                <h3 className="font-semibold text-purple-900 mb-3">Billing</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-purple-600">Booked Duration</span>
                    <span className="text-purple-900">{SLOT_LABELS[selectedBooking.durationSlot]}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-purple-600">Room Charges (Subtotal)</span>
                    <span className="text-purple-900 flex items-center gap-0.5">
                      <IndianRupee className="w-3 h-3" />
                      {selectedBooking.subtotal || selectedBooking.price}
                    </span>
                  </div>
                  {selectedBooking.gstPercentage > 0 && (
                    <div className="flex justify-between">
                      <span className="text-purple-600">GST @ {selectedBooking.gstPercentage}%</span>
                      <span className="text-purple-900 flex items-center gap-0.5">
                        <IndianRupee className="w-3 h-3" />
                        {selectedBooking.gstAmount || 0}
                      </span>
                    </div>
                  )}
                  {selectedBooking.extraCharges > 0 && (
                    <div className="flex justify-between text-orange-700">
                      <span>Extra Charges {selectedBooking.extraChargesNote && `(${selectedBooking.extraChargesNote})`}</span>
                      <span className="flex items-center gap-0.5">
                        <IndianRupee className="w-3 h-3" />
                        {selectedBooking.extraCharges}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t border-purple-200 pt-2 mt-2">
                    <span className="text-purple-900">Total Amount</span>
                    <span className="text-purple-900 flex items-center gap-0.5">
                      <IndianRupee className="w-4 h-4" />
                      {selectedBooking.totalAmount}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={() => setIsViewModalOpen(false)}
              className="btn-secondary w-full"
            >
              Close
            </button>
          </div>
        )}
      </Modal>

      {/* Checkout Modal */}
      <Modal
        isOpen={isCheckoutModalOpen}
        onClose={() => setIsCheckoutModalOpen(false)}
        title="Checkout Guest"
      >
        {selectedBooking && (
          <div className="space-y-4">
            {/* Guest Info */}
            <div className="p-4 bg-midnight-50 rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                <User className="w-5 h-5 text-midnight-500" />
                <span className="font-semibold text-midnight-900">{selectedBooking.guestName}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-midnight-500">Room:</span>
                  <span className="ml-2 font-medium text-midnight-900">
                    {selectedBooking.allocatedRoom?.roomNumber}
                  </span>
                </div>
                <div>
                  <span className="text-midnight-500">Duration:</span>
                  <span className="ml-2 font-medium text-midnight-900">
                    {SLOT_LABELS[selectedBooking.durationSlot]}
                  </span>
                </div>
              </div>
            </div>

            {/* Adjustments — additions and deductions are separate, clearly-labelled fields */}
            <ChargeAdjustments
              extraCharges={extraCharges}
              extraChargesNote={extraChargesNote}
              discount={discount}
              discountNote={discountNote}
              maxDiscount={checkoutBill.grossAmount}
              onChange={({ extraCharges: ec, extraChargesNote: en, discount: d, discountNote: dn }) => {
                setExtraCharges(ec);
                setExtraChargesNote(en);
                setDiscount(d);
                setDiscountNote(dn);
              }}
            />

            {/* Bill preview — recalculated live from the same helper the server uses */}
            <div className="p-4 bg-green-50 rounded-xl border border-green-200">
              <h4 className="font-semibold text-green-900 mb-3">Bill Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-green-700">Room Charges (Subtotal)</span>
                  <span className="font-medium text-green-900 flex items-center gap-0.5">
                    <IndianRupee className="w-3 h-3" />{checkoutBill.subtotal}
                  </span>
                </div>
                {checkoutBill.extraCharges > 0 && (
                  <div className="flex justify-between text-orange-700">
                    <span>Extra Charges{extraChargesNote ? ` (${extraChargesNote})` : ''}</span>
                    <span className="font-medium flex items-center gap-0.5">
                      <IndianRupee className="w-3 h-3" />{checkoutBill.extraCharges}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-green-700">GST @ {checkoutBill.gstPercentage}%</span>
                  <span className="font-medium text-green-900 flex items-center gap-0.5">
                    <IndianRupee className="w-3 h-3" />{checkoutBill.gstAmount}
                  </span>
                </div>
                {checkoutBill.discount > 0 && (
                  <div className="flex justify-between text-green-700 font-medium">
                    <span>Discount{discountNote ? ` (${discountNote})` : ''}</span>
                    <span className="flex items-center gap-0.5">
                      − <IndianRupee className="w-3 h-3" />{checkoutBill.discount}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t border-green-300 pt-2 mt-2">
                  <span className="text-green-900">Total Amount</span>
                  <span className="text-green-900 flex items-center gap-0.5">
                    <IndianRupee className="w-4 h-4" />{checkoutBill.totalAmount}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setIsCheckoutModalOpen(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleCheckOut}
                disabled={checkingOut}
                className="btn-primary flex-1"
              >
                {checkingOut ? 'Checking out…' : 'Complete Checkout'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Admin Create Booking Modal — passed the whole station so the modal can name the
          station it is booking for, not just scope its data to it. */}
      <AdminBookingModal
        isOpen={isAdminBookingModalOpen}
        onClose={() => setIsAdminBookingModalOpen(false)}
        station={activeStation}
        stationId={selectedStation}
        onSuccess={fetchData}
      />
    </AdminLayout>
  );
};

export default BookingRequests;
